/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnalyticsProvider } from '@services/analytics-provider';
import { TRACKING_ENABLED } from '@services/environment';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Google Analytics provider implementation
 */
export class GoogleAnalyticsProvider implements AnalyticsProvider {
  private readonly GA_TRACKING_ID = 'AW-17226271277';
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private shouldTrack(): boolean {
    return TRACKING_ENABLED;
  }

  private initialize() {
    if (
      !this.shouldTrack() ||
      this.isInitialized ||
      typeof window === 'undefined'
    ) {
      return;
    }

    // Load Google Analytics script dynamically
    this.loadGoogleAnalytics()
      .then(() => {
        this.isInitialized = true;
      })
      .catch(error => {
        console.warn('Failed to load Google Analytics:', error);
      });
  }

  private loadGoogleAnalytics(): Promise<void> {
    return new Promise((resolve, reject) => {
      // If gtag already exists, we're good
      if (typeof window.gtag === 'function') {
        resolve();
        return;
      }

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];

      // Create gtag function
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(args);
      };

      // Load the GA script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_TRACKING_ID}`;

      script.onload = () => {
        // Initialize GA
        window.gtag('js', new Date());
        window.gtag('config', this.GA_TRACKING_ID);
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics script'));
      };

      document.head.appendChild(script);
    });
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.isInitialized || typeof window.gtag !== 'function') {
      console.warn('Google Analytics not initialized');
      return;
    }

    try {
      const sanitizedProperties = this.sanitizeProperties(properties || {});

      // Convert PostHog-style properties to GA-style parameters
      const gaParams: Record<string, any> = {
        event_category: this.mapEventCategory(eventName, sanitizedProperties),
        event_label: this.extractEventLabel(sanitizedProperties),
        value: this.extractNumericValue(sanitizedProperties),
      };

      // Add custom parameters
      Object.entries(sanitizedProperties).forEach(([key, value]) => {
        if (!['event_category', 'event_label', 'value'].includes(key)) {
          gaParams[key] = value;
        }
      });

      // Remove undefined values
      Object.keys(gaParams).forEach(key => {
        if (gaParams[key] === undefined || gaParams[key] === null) {
          delete gaParams[key];
        }
      });

      window.gtag('event', this.mapEventName(eventName), gaParams);
    } catch (error) {
      console.warn('Failed to track GA event:', error);
    }
  }

  trackPageView(pageName: string, properties?: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.isInitialized || typeof window.gtag !== 'function') {
      return;
    }

    try {
      window.gtag('config', this.GA_TRACKING_ID, {
        page_title: pageName,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...this.sanitizeProperties(properties || {}),
      });
    } catch (error) {
      console.warn('Failed to track GA page view:', error);
    }
  }

  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.isInitialized || typeof window.gtag !== 'function') {
      return;
    }

    try {
      window.gtag('config', this.GA_TRACKING_ID, {
        user_id: userId,
        user_properties: this.sanitizeProperties(properties || {}),
      });
    } catch (error) {
      console.warn('Failed to identify user in GA:', error);
    }
  }

  updateUserProfile(properties: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.isInitialized || typeof window.gtag !== 'function') {
      return;
    }

    try {
      window.gtag('config', this.GA_TRACKING_ID, {
        user_properties: this.sanitizeProperties(properties),
      });
    } catch (error) {
      console.warn('Failed to update user profile in GA:', error);
    }
  }

  trackError(error: Error | unknown, context?: Record<string, unknown>): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorName = error instanceof Error ? error.name : 'Unknown';

    this.trackEvent('exception', {
      event_category: 'error',
      event_label: errorName,
      error_message: errorMessage,
      fatal: false,
      ...context,
    });
  }

  reset(): void {
    if (!this.shouldTrack()) {
      return;
    }

    // GA doesn't have a direct reset method like PostHog
    // We can clear user identification by setting user_id to null
    if (this.isInitialized && typeof window.gtag === 'function') {
      try {
        window.gtag('config', this.GA_TRACKING_ID, {
          user_id: null,
        });
      } catch (error) {
        console.warn('Failed to reset GA:', error);
      }
    }
  }

  private sanitizeProperties(
    properties: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(properties)) {
      // Skip sensitive data, functions, or overly complex objects
      if (typeof value === 'function') continue;
      if (key.toLowerCase().includes('password')) continue;
      if (key.toLowerCase().includes('ssn')) continue;
      if (key.toLowerCase().includes('social')) continue;
      if (key.toLowerCase().includes('stack')) continue; // Skip error stack traces

      // Only include primitive values or simple objects for GA
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        value === null
      ) {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        // Convert arrays to comma-separated strings for GA
        sanitized[key] = value.join(', ');
      } else if (value && typeof value === 'object') {
        // Flatten simple objects
        sanitized[key] = JSON.stringify(value);
      }
    }

    return sanitized;
  }

  private mapEventName(eventName: string): string {
    // Map PostHog event names to GA-friendly names
    const eventMap: Record<string, string> = {
      onboarding_flow_started: 'begin_onboarding',
      onboarding_step_completed: 'onboarding_progress',
      onboarding_flow_completed: 'complete_onboarding',
      form_field_interaction: 'form_interaction',
      investment_selected: 'select_content',
      tax_savings_calculated: 'calculate_tax_savings',
      page_viewed: 'page_view',
      error_occurred: 'exception',
    };

    return eventMap[eventName] || eventName;
  }

  private mapEventCategory(
    eventName: string,
    properties: Record<string, unknown>
  ): string {
    // Determine event category based on event name or properties
    if (eventName.includes('onboarding')) return 'onboarding';
    if (eventName.includes('form')) return 'form';
    if (eventName.includes('cta') || eventName.includes('button'))
      return 'engagement';
    if (eventName.includes('user')) return 'user';
    if (eventName.includes('error') || eventName.includes('exception'))
      return 'error';
    if (eventName.includes('page')) return 'navigation';
    if (properties.event_category) return String(properties.event_category);

    return 'general';
  }

  private extractEventLabel(
    properties: Record<string, unknown>
  ): string | undefined {
    // Extract a meaningful label from properties
    if (properties.event_label) return String(properties.event_label);
    if (properties.step_name) return String(properties.step_name);
    if (properties.field_name) return String(properties.field_name);
    if (properties.page_name) return String(properties.page_name);
    if (properties.cta_name) return String(properties.cta_name);

    return undefined;
  }

  private extractNumericValue(
    properties: Record<string, unknown>
  ): number | undefined {
    // Extract numeric values for GA's value parameter
    if (typeof properties.value === 'number') return properties.value;
    if (typeof properties.estimated_savings === 'number')
      return properties.estimated_savings;
    if (typeof properties.annual_income === 'number')
      return properties.annual_income;
    if (typeof properties.step_number === 'number')
      return properties.step_number;

    return undefined;
  }
}
